import { useState, useCallback, useRef } from 'react';
import { useEditorStore } from '@/store/editorStore';
import { useDeployStore } from '@/store/deployStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import * as aiService from '@/services/ai.service';
import * as fileService from '@/services/file.service';

/* ================================================================== */
/*  Pipeline types                                                    */
/* ================================================================== */

export type PipelineStage =
  | 'idle'
  | 'generating'
  | 'creating-file'
  | 'compiling'
  | 'fixing'
  | 'deploying'
  | 'complete'
  | 'error';

export interface PipelineState {
  stage: PipelineStage;
  progress: number;        // 0-100
  message: string;
  contractName?: string;
  contractCode?: string;
  contractAddress?: string;
  network?: string;
  error?: string;
  durationMs?: number;
}

const INITIAL_STATE: PipelineState = {
  stage: 'idle',
  progress: 0,
  message: '',
};

/* ================================================================== */
/*  Hook                                                              */
/* ================================================================== */

/**
 * useAiDeployPipeline — the "undeniable capability" hook.
 *
 * One function call chains the entire lifecycle:
 *   Prompt → AI generates contract → file created → save → compile
 *   → (auto-fix if errors, up to 2 retries) → deploy → done.
 *
 * Returns reactive state so UI can show real-time progress.
 */
export function useAiDeployPipeline() {
  const [state, setState] = useState<PipelineState>(INITIAL_STATE);
  const abortRef = useRef(false);

  const { openFile, updateFileContent } = useEditorStore();
  const { compile, deploy } = useDeployStore();
  const { activeWorkspace } = useWorkspaceStore();

  const update = useCallback((patch: Partial<PipelineState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  /**
   * Run the full pipeline: prompt → deployed contract.
   */
  const run = useCallback(
    async (prompt: string, options?: { network?: string }) => {
      const workspaceId = activeWorkspace?.id;
      if (!workspaceId) {
        update({ stage: 'error', error: 'No active workspace', message: 'No workspace found' });
        return;
      }

      abortRef.current = false;
      const startTime = Date.now();
      const network = options?.network ?? 'local';

      // ── Stage 1: AI Generate ──────────────────────────────────
      update({ stage: 'generating', progress: 10, message: 'AI is generating your contract...' });

      let code: string;
      let contractName: string;
      try {
        const res = await aiService.generate(prompt, {});
        code = res.code;
        contractName = inferContractName(code);
        update({
          stage: 'generating',
          progress: 30,
          message: `Generated ${contractName}`,
          contractName,
          contractCode: code,
        });
      } catch (err) {
        update({ stage: 'error', error: String(err), message: 'AI generation failed' });
        return;
      }
      if (abortRef.current) return;

      // ── Stage 2: Create file ──────────────────────────────────
      update({ stage: 'creating-file', progress: 40, message: `Creating ${contractName}.sol...` });

      const filePath = `contracts/${contractName}.sol`;
      try {
        await fileService.createFile(workspaceId, filePath, 'file');
        await fileService.saveFile(workspaceId, filePath, code);
        openFile(filePath, code);
      } catch {
        // File may already exist — update its content
        try {
          await fileService.saveFile(workspaceId, filePath, code);
          openFile(filePath, code);
        } catch (err2) {
          update({ stage: 'error', error: String(err2), message: 'Failed to create file' });
          return;
        }
      }
      if (abortRef.current) return;

      // ── Stage 3: Compile (with auto-fix loop) ────────────────
      update({ stage: 'compiling', progress: 55, message: 'Compiling contract...' });

      const MAX_FIX_ATTEMPTS = 2;
      let currentCode = code;

      for (let attempt = 0; attempt <= MAX_FIX_ATTEMPTS; attempt++) {
        try {
          await compile(workspaceId);
        } catch {
          // compile() updates store — we read from there
        }

        // Read compile result from store
        const compileResult = useDeployStore.getState().compileResult;

        if (compileResult?.success) {
          update({ progress: 75, message: 'Compilation successful!' });
          break;
        }

        // Compile failed
        if (attempt < MAX_FIX_ATTEMPTS) {
          update({
            stage: 'fixing',
            progress: 55 + (attempt + 1) * 5,
            message: `Fixing compile errors (attempt ${attempt + 1}/${MAX_FIX_ATTEMPTS})...`,
          });

          try {
            const errorMessages = compileResult?.errors?.map((e) => e.message) ?? ['Unknown error'];
            const fixRes = await aiService.fix(currentCode, errorMessages.join('\n'), {});
            if (fixRes.code) {
              currentCode = fixRes.code;
              updateFileContent(filePath, currentCode);
              await fileService.saveFile(workspaceId, filePath, currentCode);
              update({ contractCode: currentCode });
            }
          } catch {
            // Fix failed — continue to next attempt or bail
          }
        } else {
          // Exhausted fix attempts
          update({
            stage: 'error',
            progress: 60,
            error: compileResult?.errors?.[0]?.message ?? 'Compilation failed',
            message: 'Could not fix compile errors automatically',
          });
          return;
        }
        if (abortRef.current) return;
      }

      // ── Stage 4: Deploy ───────────────────────────────────────
      update({ stage: 'deploying', progress: 80, message: `Deploying to ${network}...` });

      try {
        await deploy(workspaceId, { network });
      } catch {
        // deploy() updates store
      }

      const deployResult = useDeployStore.getState().deployResult;
      if (!deployResult?.success) {
        update({
          stage: 'error',
          progress: 85,
          error: deployResult?.error ?? 'Deployment failed',
          message: 'Deployment failed',
        });
        return;
      }

      // ── Complete ──────────────────────────────────────────────
      const durationMs = Date.now() - startTime;
      update({
        stage: 'complete',
        progress: 100,
        message: `Deployed ${contractName} successfully!`,
        contractAddress: deployResult.contractAddress,
        network,
        durationMs,
      });
    },
    [activeWorkspace?.id, compile, deploy, openFile, updateFileContent, update],
  );

  const abort = useCallback(() => {
    abortRef.current = true;
    update({ stage: 'error', message: 'Pipeline cancelled' });
  }, [update]);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return { state, run, abort, reset };
}

/* ================================================================== */
/*  Helpers                                                           */
/* ================================================================== */

function inferContractName(code: string): string {
  const match = code.match(/contract\s+(\w+)/);
  if (match) return match[1];
  const iMatch = code.match(/interface\s+(\w+)/);
  if (iMatch) return iMatch[1];
  return `Contract_${Date.now().toString(36)}`;
}
